import { snakeCase } from 'lodash';
import { useMemo } from 'react';
import { useAccountDataQuery } from 'queries/account';
import { AccountFeature, useAccountFeature } from './use-account-feature';
import { useLocalStorage } from './use-local-storage';
export var PrereleaseFeature = /*#__PURE__*/ function(PrereleaseFeature) {
    PrereleaseFeature["ShiftStartDateInResourceManagement"] = "ShiftStartDateInResourceManagement";
    PrereleaseFeature["PaginateAllTask"] = "PaginateAllTask";
    PrereleaseFeature["InferredProgress"] = "InferredProgress";
    PrereleaseFeature["Gpt"] = "Gpt";
    PrereleaseFeature["ProjectListPagination"] = "ProjectListPagination";
    PrereleaseFeature["ProjectTasksListPagination"] = "ProjectTasksListPagination";
    PrereleaseFeature["ShowSubscriptionPeriodHoursCarryOver"] = "ShowSubscriptionPeriodHoursCarryOver";
    PrereleaseFeature["MeetingsAI"] = "MeetingsAI";
    PrereleaseFeature["AccountsPortfolio"] = "AccountsPortfolio";
    PrereleaseFeature["CustomerPortalTemplateField"] = "CustomerPortalTemplateField";
    PrereleaseFeature["Spotlight"] = "Spotlight";
    PrereleaseFeature["HideFormulaFields"] = "HideFormulaFields";
    PrereleaseFeature["CpOverride"] = "CpOverride";
    PrereleaseFeature["SpotlightOverride"] = "SpotlightOverride";
    PrereleaseFeature["Marketplace"] = "Marketplace";
    PrereleaseFeature["TSReportingLine"] = "TSReportingLine";
    PrereleaseFeature["MultiLineBudgets"] = "MultiLineBudgets";
    PrereleaseFeature["MultipleMeetingsInAiFill"] = "MultipleMeetingsInAiFill";
    // LanguageConfigurations = 'LanguageConfigurations',
    PrereleaseFeature["AiFillContentStreaming"] = "AiFillContentStreaming";
    PrereleaseFeature["ExpenseManagement"] = "ExpenseManagement";
    PrereleaseFeature["Sheets"] = "Sheets";
    PrereleaseFeature["AntdRlUISwitcher"] = "AntdRlUISwitcher";
    PrereleaseFeature["TaskPaneUIRevamp"] = "TaskPaneUIRevamp";
    PrereleaseFeature["CurrentPhasesOptimized"] = "CurrentPhasesOptimized";
    PrereleaseFeature["ProjectListColumnPagination"] = "ProjectListColumnPagination";
    PrereleaseFeature["ScheduledExports"] = "ScheduledExports";
    PrereleaseFeature["EnableCronAutomation"] = "EnableCronAutomation";
    PrereleaseFeature["SlackPvtChannelsFeature"] = "SlackPvtChannelsFeature";
    PrereleaseFeature["TimesheetPartnerTimeLimit"] = "TimesheetPartnerTimeLimit";
    PrereleaseFeature["NitroClawDownload"] = "NitroClawDownload";
    return PrereleaseFeature;
}({});
export const allowed_hostnames = [
    'localhost',
    'northstar.nitro.run',
    'bangalore.nitro.run',
    'testing.nitro.run',
    'rmroles.orbits.run',
    'deepakkumar.nitro.run',
    'report1.orbits.run',
    'report2.orbits.run'
];
const default_meetings_ai_enabled_hostnames = [
    'success.rocketlane.com'
];
export const default_spotlight_override_enabled_hostnames = [
    'phorest.rocketlane.com',
    'onboarding.phorest.com'
];
export const excluded_features_localhost = window.Cypress ? [
    "PaginateAllTask"
] : [];
export const useFeature = (feature, offByDefault = false)=>{
    const [value] = useLocalStorage(`__features.v2.${snakeCase(feature)}`, !offByDefault && allowed_hostnames.includes(window.location.hostname));
    // added for cypress - need to remove once the feature is live
    if (window.location.hostname.includes('localhost') && window.Cypress) {
        return !excluded_features_localhost.includes(feature);
    }
    // enable meeting ai by default for success.rocketlane.com
    if ([
        "MeetingsAI"
    ].includes(feature) && default_meetings_ai_enabled_hostnames.includes(window.location.hostname)) {
        return true;
    }
    // TODO: Remove this once the feature is live
    if (feature === "SpotlightOverride" || feature === "Marketplace" || feature === "CpOverride") {
        return true;
    }
    return value;
};
export const useIsCustomerPortalEnabled = ()=>{
    return useMemo(()=>true, []);
};
export const useIsTaskPaneOptimizationEnabled = ()=>{
    return true;
};
// const BETA_ACCOUNTS = [107, 1];
export const useNewDashboardEnabled = ()=>{
    var _ref;
    var _data_accountMeta_features, _data_accountMeta;
    const { data, isLoading, isError } = useAccountDataQuery();
    if (isLoading || isError) {
        return false;
    }
    // if (BETA_ACCOUNTS.includes(account.accountId)) {
    //   return true;
    // }
    return (_ref = data === null || data === void 0 ? void 0 : (_data_accountMeta = data.accountMeta) === null || _data_accountMeta === void 0 ? void 0 : (_data_accountMeta_features = _data_accountMeta.features) === null || _data_accountMeta_features === void 0 ? void 0 : _data_accountMeta_features.dashboardV2) !== null && _ref !== void 0 ? _ref : false;
};
const CURRENT_PHASES_OPTIMIZED_ACCOUNTS = [
    22192
];
export const useIsCurrentPhasesOptimizedEnabled = ()=>{
    var _initData_account;
    const isEnabled = useFeature("CurrentPhasesOptimized");
    const { data: initData } = useAccountDataQuery();
    const accountId = initData === null || initData === void 0 ? void 0 : (_initData_account = initData.account) === null || _initData_account === void 0 ? void 0 : _initData_account.accountId;
    if (accountId && CURRENT_PHASES_OPTIMIZED_ACCOUNTS.includes(accountId)) {
        return true;
    }
    return isEnabled;
};
export const useIsEnableCronAutomationEnabled = ()=>{
    const isEnabled = useFeature("EnableCronAutomation");
    if (window.location.hostname.includes('localhost')) {
        return false;
    }
    return isEnabled;
};
export const useIsCreditNotesFeatureEnabled = ()=>{
    var _ref;
    var _data_accountMeta_features, _data_accountMeta;
    const { data } = useAccountDataQuery();
    const [isCreditNotesFeatureEnabled] = useAccountFeature(AccountFeature.CreditNotes);
    return (_ref = isCreditNotesFeatureEnabled !== null && isCreditNotesFeatureEnabled !== void 0 ? isCreditNotesFeatureEnabled : data === null || data === void 0 ? void 0 : (_data_accountMeta = data.accountMeta) === null || _data_accountMeta === void 0 ? void 0 : (_data_accountMeta_features = _data_accountMeta.features) === null || _data_accountMeta_features === void 0 ? void 0 : _data_accountMeta_features.creditNotes) !== null && _ref !== void 0 ? _ref : false;
};
export const useScheduledExportsEnabled = ()=>{
    var _ref;
    var _data_accountMeta_features, _data_accountMeta;
    const { data, isLoading, isError } = useAccountDataQuery();
    if (isLoading || isError) {
        return false;
    }
    return (_ref = data === null || data === void 0 ? void 0 : (_data_accountMeta = data.accountMeta) === null || _data_accountMeta === void 0 ? void 0 : (_data_accountMeta_features = _data_accountMeta.features) === null || _data_accountMeta_features === void 0 ? void 0 : _data_accountMeta_features.scheduledExports) !== null && _ref !== void 0 ? _ref : false;
};
export const useGtmCodeBlockEnabled = ()=>{
    var _ref;
    var _data_accountMeta_features, _data_accountMeta;
    const { data, isLoading, isError } = useAccountDataQuery();
    if (isLoading || isError) {
        return false;
    }
    return (_ref = data === null || data === void 0 ? void 0 : (_data_accountMeta = data.accountMeta) === null || _data_accountMeta === void 0 ? void 0 : (_data_accountMeta_features = _data_accountMeta.features) === null || _data_accountMeta_features === void 0 ? void 0 : _data_accountMeta_features.gtmCodeBlock) !== null && _ref !== void 0 ? _ref : false;
};
export const useSalesforceV2Enabled = ()=>{
    var _ref;
    var _data_accountMeta_features, _data_accountMeta;
    const { data, isLoading, isError } = useAccountDataQuery();
    if (isLoading || isError) {
        return false;
    }
    return (_ref = data === null || data === void 0 ? void 0 : (_data_accountMeta = data.accountMeta) === null || _data_accountMeta === void 0 ? void 0 : (_data_accountMeta_features = _data_accountMeta.features) === null || _data_accountMeta_features === void 0 ? void 0 : _data_accountMeta_features.salesforceIntegrationV2) !== null && _ref !== void 0 ? _ref : false;
};
